import { useTranslation } from "react-i18next";
import { Flex } from "antd";
import { useNavigate } from "react-router-dom";
import { AppButton, AppModal, AppText } from "@/shared/Components";
import { useAlert } from "@/shared/hooks";
import { useModal } from "@/features/modals/lib";
import { TrashIcon } from "@/shared/icons";
import {
  useDeletePersonMutation,
  useGetAllNotesQuery,
  useGetAllPersonsQuery,
  useGetPersonByIdQuery,
} from "@/services/api/entities";
import { AlertYellowIcon } from "@/shared/icons/AlertYellowIcon";
import { handleFullName } from "@/shared/utils/handle-contacts-null";
import { appRouterPaths } from "@/services/navigation";
import { getErrorMessage } from "@/services/errors";
import type { MaybeType } from "@/shared/types";
import styles from "./styles.module.scss";

export const DeleteContactButton = ({
  selectedContactId,
  setSelectedPersonId,
}: {
  selectedContactId: MaybeType<string>;
  setSelectedPersonId: (id: MaybeType<string>) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    showModal: showDeleteModal,
    hideModal: hideDeleteModal,
    isModalOpen: isDeleteModalOpen,
  } = useModal();
  const {
    showModal: showHasNotesModal,
    hideModal: hideHasNotesModal,
    isModalOpen: isHasNotesModalOpen,
  } = useModal();

  const { data: currentPerson } = useGetPersonByIdQuery(
    { id: selectedContactId ?? "" },
    {
      skip: !selectedContactId || selectedContactId === "TEMP_ID",
    },
  );

  const { data: notes } = useGetAllNotesQuery();
  const { data: personsData } = useGetAllPersonsQuery();

  const [deletePerson, { isLoading }] = useDeletePersonMutation();

  const { callError } = useAlert();

  const handleOpenModal = () => {
    const contactHasNotes = notes?.some((note) => note.personId === currentPerson?.id);
    if (contactHasNotes) {
      showHasNotesModal();
    } else {
      showDeleteModal();
    }
  };

  const deleteHandler = () => {
    if (!currentPerson) return;
    deletePerson(currentPerson.id)
      .unwrap()
      .then(() => {
        navigate(
          `${appRouterPaths.contacts.base}/${location.pathname.split("/")[2] ?? "generalInfo"}/${personsData?.persons[0].id}`,
        );
        setSelectedPersonId(personsData?.persons[0].id ?? "");
        hideDeleteModal();
      })
      .catch((e) => callError({ text: getErrorMessage(e.code) }));
  };

  const isTempPerson = currentPerson?.id.includes("new-");

  if (isTempPerson) {
    return null;
  }

  return (
    <>
      <AppButton
        className={styles.root}
        icon={<TrashIcon />}
        onClick={handleOpenModal}
        disabled={!selectedContactId || selectedContactId === "TEMP_ID"}
      >
        {t("contactsPage.deleteContact")}
      </AppButton>
      <AppModal
        centered
        maskClosable={false}
        closable={false}
        open={isDeleteModalOpen}
        footer={null}
        onCancel={hideDeleteModal}
        onOk={deleteHandler}
        width={360}
      >
        <Flex vertical gap={12} align="center" style={{ padding: "0 49px" }}>
          <AlertYellowIcon width={25} height={25} />
          <AppText
            size="md"
            weight="medium"
            style={{ textAlign: "center" }}
            className={styles["modal-text"]}
          >
            {t("contactsPage.deleteModal", {
              value: handleFullName(currentPerson?.firstName, currentPerson?.lastName),
            })}
          </AppText>
          <Flex justify="center" gap={12}>
            <AppButton onClick={hideDeleteModal}>{t("common.cancel")}</AppButton>
            <AppButton type="primary" onClick={deleteHandler} loading={isLoading}>
              {t("common.delete")}
            </AppButton>
          </Flex>
        </Flex>
      </AppModal>
      <AppModal
        centered
        maskClosable={false}
        closable={false}
        open={isHasNotesModalOpen}
        footer={null}
      >
        <Flex vertical gap={12} align="center" style={{ padding: "0 49px" }}>
          <AlertYellowIcon width={25} height={25} />
          <AppText
            size="sm"
            weight="medium"
            style={{ textAlign: "center" }}
            className={styles["modal-text"]}
          >
            {t("contactsPage.hasNotes")}
          </AppText>
          <Flex justify="center" gap={12}>
            <AppButton type="primary" onClick={hideHasNotesModal}>
              {t("common.gotIt")}
            </AppButton>
          </Flex>
        </Flex>
      </AppModal>
    </>
  );
};
